import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNotificacoesBKO } from '@/hooks/useNotificacoesBKO';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

export function NotificacoesBadge() {
  const navigate = useNavigate();
  const { 
    notificacoes, 
    novasNotificacoes, 
    marcarComoLida,
    marcarTodasComoLidas 
  } = useNotificacoesBKO();

  const handleNotificacaoClick = (notif: any) => {
    marcarComoLida(notif.id);
    if (notif.pedido_id) {
      navigate(`/pedidos/${notif.pedido_id}`);
    }
  };

  const getIcone = (tipo: string) => {
    const icones: Record<string, string> = {
      novo_pedido_contrato: '📋',
      novo_pedido_input: '🚀',
      pedido_transferido: '✅',
      leads_recebidos: '👥',
      leads_transferidos: '🔄',
    };
    return icones[tipo] || '🔔';
  };

  const getCorBorda = (prioridade: string) => {
    const cores: Record<string, string> = {
      urgente: 'border-danger',
      alta: 'border-warning',
      normal: 'border-info',
      baixa: 'border-border'
    };
    return cores[prioridade] || 'border-info';
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {novasNotificacoes > 0 && (
            <span className="absolute -top-1 -right-1 bg-danger text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold animate-pulse">
              {novasNotificacoes > 9 ? '9+' : novasNotificacoes}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent className="w-96 p-0" align="end">
        <div className="p-4 border-b flex items-center justify-between bg-muted/50">
          <div>
            <h3 className="font-bold text-lg">Notificações</h3>
            {novasNotificacoes > 0 && (
              <p className="text-xs text-muted-foreground">
                {novasNotificacoes} nova{novasNotificacoes > 1 ? 's' : ''}
              </p>
            )}
          </div>
          {novasNotificacoes > 0 && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={marcarTodasComoLidas}
              className="text-xs"
            >
              Marcar todas como lidas
            </Button>
          )}
        </div>
        
        <ScrollArea className="h-[400px]">
          {notificacoes.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Bell className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Nenhuma notificação nova</p>
            </div>
          ) : (
            <div className="divide-y">
              {notificacoes.map(notif => (
                <div 
                  key={notif.id} 
                  className={`p-4 hover:bg-muted/50 cursor-pointer transition-colors border-l-4 ${getCorBorda(notif.prioridade)}`}
                  onClick={() => handleNotificacaoClick(notif)}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-2xl flex-shrink-0">
                      {getIcone(notif.tipo)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm">
                        {notif.titulo}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {notif.mensagem}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(notif.created_at), {
                            addSuffix: true,
                            locale: ptBR
                          })}
                        </p>
                        {notif.prioridade === 'urgente' && (
                          <span className="text-xs bg-danger/10 text-danger px-2 py-0.5 rounded-full font-medium">
                            Urgente
                          </span>
                        )}
                        {notif.prioridade === 'alta' && (
                          <span className="text-xs bg-warning/10 text-warning px-2 py-0.5 rounded-full font-medium">
                            Alta
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
